/**
 * Shared cycle status text component.
 * Single source of truth for how cycle state is displayed across the app.
 * Used by: CycleTracker, Health.tsx, HealthSummaryPanel, and anywhere else
 * that shows cycle phase/day/countdown text.
 */

import { getPhaseLabel } from '../../utils/cyclePrediction';
import type { CyclePrediction } from '../../utils/cyclePrediction';
import { CONTRACEPTION_PROFILES } from '@researcher/shared';
import type { ContraceptionCategory } from '@researcher/shared';

interface CycleStatusTextProps {
  prediction: CyclePrediction;
  /** 'full' shows both title + subtitle. 'compact' shows single line. */
  variant?: 'full' | 'compact';
  titleClassName?: string;
  subtitleClassName?: string;
}

function getBleedingLabel(prediction: CyclePrediction): string {
  if (prediction.contraceptionCategory) {
    const profile = CONTRACEPTION_PROFILES[prediction.contraceptionCategory as ContraceptionCategory];
    if (profile?.hasWithdrawalBleed) return 'Withdrawal bleed';
    if (profile && profile.category !== 'none' && profile.category !== 'other' && profile.hormonalType !== 'non_hormonal') return 'Bleeding';
  }
  return 'Period';
}

export function getCycleStatusTitle(prediction: CyclePrediction): string {
  const bleedLabel = getBleedingLabel(prediction);
  // Projected menstrual days are handled by the currentPhase === 'menstrual'
  // branch below (currentCycleDay is the projected day) — no special case needed.
  if (prediction.currentPhase === 'menstrual') {
    return `${bleedLabel} day ${prediction.currentCycleDay}`;
  }
  // For non-cyclical BC, show days on method instead of phase name
  if (prediction.isOnContraception && prediction.daysOnMethod != null && prediction.fertileWindowSuppressed) {
    return getPhaseLabel(prediction.currentPhase);
  }
  return getPhaseLabel(prediction.currentPhase);
}

export function getCycleStatusSubtitle(prediction: CyclePrediction): string {
  const bleedLabel = getBleedingLabel(prediction);

  // Show days on method for non-cyclical hormonal BC
  if (prediction.isOnContraception && prediction.daysOnMethod != null && prediction.fertileWindowSuppressed) {
    return `Day ${prediction.daysOnMethod} on method`;
  }

  if (prediction.currentPhase === 'menstrual') {
    return `Cycle day ${prediction.currentCycleDay} of ${prediction.averageCycleLength}`;
  }
  // daysUntilNextPeriod is always >= 1 (the engine rolls cycles forward).
  return `Day ${prediction.currentCycleDay} · ${bleedLabel} in ${prediction.daysUntilNextPeriod}d`;
}

export default function CycleStatusText({
  prediction,
  variant = 'full',
  titleClassName = 'text-sm font-bold text-gray-800 dark:text-gray-100 leading-tight',
  subtitleClassName = 'text-xs text-gray-500 dark:text-gray-400 mt-0.5',
}: CycleStatusTextProps) {
  const title = getCycleStatusTitle(prediction);
  const subtitle = getCycleStatusSubtitle(prediction);

  if (variant === 'compact') {
    return <span className={subtitleClassName}>{title}</span>;
  }

  return (
    <>
      <p className={titleClassName}>{title}</p>
      <p className={subtitleClassName}>{subtitle}</p>
    </>
  );
}
